/* Rivox Bundle Builder
   Subscription-first multi-flavor bundle builder.
   Mirrors theme cart conventions: routes.cart_add_url, cart drawer re-render, PUB_SUB cartUpdate. */

if (!customElements.get("rivox-bundle-builder")) {
  customElements.define(
    "rivox-bundle-builder",
    class RivoxBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.minCans = parseInt(this.dataset.minCans, 10) || 3;
        try {
          this.tiers = JSON.parse(this.dataset.tiers) || [];
        } catch (e) {
          this.tiers = [];
        }
        this.tiers.sort((a, b) => a.min - b.min);
        this.cartUrl = this.dataset.cartUrl || "/cart/add";
        this.freeshipCents = parseInt(this.dataset.freeshipCents, 10) || 0;
        /* optional: only surface subscription plans whose name contains this
           text (set in the theme editor) so the frequency dropdown shows one
           clean plan instead of every selling plan on the product. */
        this.planFilter = (this.dataset.planFilter || "").trim().toLowerCase();
        /* when true, the subscription plan is auto-chosen by quantity tier:
           the plan whose name contains the tier's % is assigned at checkout,
           so 3/6/9 cans map to e.g. Monthly 35/40/45 flat plans in Loop. */
        this.subscribeAutoTier = this.dataset.subscribeAutoTier === "true";
        /* during preorder, Stoq forces its flat preorder plan onto every
           subscription, so subscribe is charged a single rate regardless of
           qty. Show that honest rate instead of the post-launch tiers. */
        this.preorderSubPct = parseInt(this.dataset.preorderSubPct, 10) || 0;
        /* hide the Subscribe & Save option entirely (e.g. during preorder) —
           builder behaves as one-time only. */
        this.hideSubscribe = this.dataset.hideSubscribe === "true";
        /* unit noun shown to customers ("can"/"cans" for pouches, "box"/"boxes"
           for gum). Internal variables still say "cans". */
        this.unitSingular = this.dataset.unitSingular || "can";
        this.unitPlural = this.dataset.unitPlural || "cans";
        try {
          this.tierCodes = (JSON.parse(this.dataset.tierCodes) || [])
            .filter((t) => t.code)
            .sort((a, b) => a.min - b.min);
        } catch (e) {
          this.tierCodes = [];
        }

        this.ladder = this.querySelector("[data-ladder]");
        this.ladderFill = this.querySelector("[data-ladder-fill]");
        this.ladderTiers = this.querySelector("[data-ladder-tiers]");
        this.offBadge = this.querySelector("[data-off-badge]");
        this.freeshipEl = this.querySelector("[data-freeship]");
        this.buildLadder();

        this.flavors = Array.from(this.querySelectorAll("[data-flavor]"));
        this.summary = this.querySelector("[data-summary]");
        this.priceCompareEl = this.querySelector("[data-price-compare]");
        this.priceFinalEl = this.querySelector("[data-price-final]");
        this.savingsEl = this.querySelector("[data-savings]");
        this.cta = this.querySelector("[data-cta]");
        this.ctaText = this.querySelector("[data-cta-text]");
        this.ctaError = this.querySelector("[data-cta-error]");
        this.tierHint = this.querySelector("[data-tier-hint]");
        this.purchaseTypeWrap = this.querySelector("[data-purchase-type]");
        this.frequencyWrap = this.querySelector("[data-frequency-wrap]");
        this.frequencySelect = this.querySelector("[data-frequency]");
        this.lineup = this.querySelector("[data-lineup]");
        this.lineupCans = this.querySelector("[data-lineup-cans]");
        this.lastFinal = 0;

        this.ctaLockedTemplate = this.ctaText ? this.ctaText.textContent : "";
        this.purchaseType = "subscribe";
        this.state = new Map(); // variantId -> { qty, price, plans, crossSell }

        this.flavors.forEach((row) => {
          this.state.set(row.dataset.variantId, {
            qty: 0,
            price: parseInt(row.dataset.price, 10) || 0,
            plans: this.parsePlans(row.dataset.plans),
            crossSell: row.hasAttribute("data-cross-sell"),
            row,
          });
        });

        /* preorder mode: section setting, or Stoq pre-order markers on the page */
        this.preorder =
          this.dataset.preorder === "true" ||
          !!document.querySelector('[data-stoq-preorder], stoq-preorder, [data-stoq-preorder="true"]');

        /* Subscriptions are available whenever real selling plans (Loop) are
           attached — including during preorder, so launch-batch subscriptions
           are captured now and billed by Loop at fulfillment. Subscribe stays
           the default (subscription-first); we only fall back to one-time when
           no plans exist yet. */
        this.hasPlans =
          !this.hideSubscribe &&
          Array.from(this.state.values()).some((s) => s.plans.length > 0);
        this.purchaseType = this.hasPlans ? "subscribe" : "onetime";

        /* unique list of available (filtered) selling plans */
        const planSeen = new Set();
        this.allPlans = [];
        this.state.forEach((s) => {
          s.plans.forEach((p) => {
            if (!planSeen.has(p.id)) {
              planSeen.add(p.id);
              this.allPlans.push(p);
            }
          });
        });

        this.initSteppers();
        this.initPurchaseType();
        this.initFrequency();
        this.initGallery();
        this.initTabs();
        this.initBatchBar();
        this.initStickyBar();

        if (this.cta) this.cta.addEventListener("click", () => this.addToCart());
        this.render();
      }

      parsePlans(raw) {
        try {
          let plans = JSON.parse(raw) || [];
          if (this.planFilter) {
            plans = plans.filter((p) =>
              (p.name || "").toLowerCase().includes(this.planFilter),
            );
          }
          return plans;
        } catch (e) {
          return [];
        }
      }

      /* ---------- steppers ---------- */
      initSteppers() {
        this.flavors.forEach((row) => {
          row.querySelectorAll("[data-step]").forEach((btn) => {
            btn.addEventListener("click", () => {
              const entry = this.state.get(row.dataset.variantId);
              const delta = parseInt(btn.dataset.step, 10);
              entry.qty = Math.max(0, Math.min(24, entry.qty + delta));
              const qtyEl = row.querySelector("[data-qty]");
              qtyEl.textContent = entry.qty;
              qtyEl.classList.remove("rvx-pulse");
              void qtyEl.offsetWidth; /* restart animation */
              qtyEl.classList.add("rvx-pulse");
              row.classList.toggle("has-qty", entry.qty > 0);
              this.render();
            });
          });
        });
      }

      /* ---------- purchase type ---------- */
      initPurchaseType() {
        if (!this.purchaseTypeWrap) return;
        if (!this.hasPlans) {
          /* one-time only: force-hide (theme CSS overrides the [hidden] attr) */
          this.purchaseTypeWrap.hidden = true;
          this.purchaseTypeWrap.style.display = "none";
          return;
        }
        this.purchaseTypeWrap.hidden = false;
        this.purchaseTypeWrap.style.display = "";
        this.purchaseTypeWrap.querySelectorAll("input[type=radio]").forEach((input) => {
          input.addEventListener("change", () => {
            this.purchaseType = input.value;
            this.purchaseTypeWrap.querySelectorAll("[data-ptype]").forEach((l) => {
              l.classList.toggle("is-active", l.dataset.ptype === this.purchaseType);
            });
            if (this.frequencyWrap)
              this.frequencyWrap.hidden =
                this.subscribeAutoTier || this.purchaseType !== "subscribe";
            this.buildLadder();
            this.render();
          });
        });
      }

      /* find the selling-plan id whose name matches a given discount %
         (e.g. pct=40 matches a plan named "Monthly 40% off"). */
      planIdForPct(pct) {
        if (!this.allPlans.length) return null;
        /* best: match the plan's real discount %, computed from its price */
        const byPct = this.allPlans.find((p) => Number(p.pct) === pct);
        if (byPct) return byPct.id;
        /* fallback: the % written in the plan name */
        const byName = this.allPlans.find((p) => {
          const m = (p.name || "").match(/(\d+)\s*%/);
          return m && parseInt(m[1], 10) === pct;
        });
        if (byName) return byName.id;
        /* last resort: closest discount we do have, else first plan */
        const sorted = [...this.allPlans].sort(
          (a, b) => Math.abs(Number(a.pct) - pct) - Math.abs(Number(b.pct) - pct),
        );
        return sorted[0].id;
      }

      initFrequency() {
        if (!this.frequencySelect || !this.hasPlans) return;
        /* auto-tier mode: the plan is chosen by quantity, so the customer
           never picks one — hide the dropdown entirely. */
        if (this.subscribeAutoTier) {
          if (this.frequencyWrap) this.frequencyWrap.hidden = true;
          return;
        }
        const seen = new Set();
        this.state.forEach((entry) => {
          entry.plans.forEach((plan) => {
            if (seen.has(plan.id)) return;
            seen.add(plan.id);
            const opt = document.createElement("option");
            opt.value = plan.id;
            opt.textContent = plan.name;
            this.frequencySelect.appendChild(opt);
          });
        });
        if (this.frequencyWrap && seen.size > 0) this.frequencyWrap.hidden = false;
      }

      /* ---------- pricing ---------- */
      /* subscriptions discount via the "pct" tier values; one-time uses "once" */
      tierKey() {
        return this.purchaseType === "subscribe" && this.hasPlans ? "pct" : "once";
      }

      currentTierPct(cans, key) {
        key = key || this.tierKey();
        let pct = 0;
        this.tiers.forEach((t) => {
          const v = t[key] || 0;
          if (cans >= t.min && v > pct) pct = v;
        });
        return pct;
      }

      nextTier(cans) {
        const key = this.tierKey();
        return this.tiers.find((t) => cans < t.min && (t[key] || 0) > 0);
      }

      /* highest-min code the cart currently qualifies for (one-time only) */
      currentTierCode(cans) {
        let match = null;
        this.tierCodes.forEach((t) => {
          if (cans >= t.min) match = t.code;
        });
        return match;
      }

      totals() {
        let cans = 0;
        let compare = 0;
        this.state.forEach((entry) => {
          if (entry.qty === 0) return;
          compare += entry.price * entry.qty;
          /* Count ALL units — pouches AND the gum cross-sell — toward the tier.
             The automatic discounts now pool both collections (3/6/9 across the
             whole cart), so mix-and-match unlocks the same tier. */
          cans += entry.qty;
        });
        let pct = this.currentTierPct(cans);
        /* preorder + subscribe is charged Stoq's flat preorder rate */
        const preorderSub =
          this.preorder &&
          this.purchaseType === "subscribe" &&
          this.preorderSubPct > 0;
        if (preorderSub) pct = this.preorderSubPct;
        const final = Math.round(compare * (1 - pct / 100));
        return { cans, compare, final, pct, savings: compare - final, preorderSub };
      }

      money(cents) {
        return "$" + (cents / 100).toFixed(2);
      }

      /* ---------- tier ladder (Ultra-style) ---------- */
      buildLadder() {
        if (!this.ladderTiers) return;
        const key = this.tierKey();
        const visible = this.tiers.filter((t) => (t[key] || 0) > 0);
        if (!visible.length) {
          if (this.ladder) this.ladder.hidden = true;
          return;
        }
        this.ladderTiers.innerHTML = visible
          .map(
            (t) =>
              `<div class="rvx-bb__ladder-tier${t.pop ? " is-popular" : ""}" data-ladder-min="${t.min}">` +
              (t.pop ? `<span class="rvx-bb__ladder-flag">MOST POPULAR</span>` : "") +
              `<span class="rvx-bb__ladder-pct">${t[key]}%</span>` +
              `<span class="rvx-bb__ladder-cans">${t.min}+ ${this.unitPlural}</span>` +
              `</div>`,
          )
          .join("");
      }

      updateLadder(t) {
        if (!this.ladderTiers) return;
        /* preorder + subscribe: tiers don't apply yet (flat launch rate), so
           hide the escalating ladder/hint but keep the honest % badge. */
        if (t.preorderSub) {
          if (this.ladder) this.ladder.hidden = true;
          if (this.tierHint) this.tierHint.hidden = true;
          if (this.offBadge) {
            this.offBadge.hidden = t.pct <= 0;
            this.offBadge.textContent = `${t.pct}% OFF — LAUNCH RATE`;
          }
          return;
        }
        if (this.ladder) this.ladder.hidden = false;
        const key = this.tierKey();
        const visible = this.tiers.filter((x) => (x[key] || 0) > 0);
        const maxMin = visible.length ? visible[visible.length - 1].min : 1;
        const nodes = this.ladderTiers.querySelectorAll("[data-ladder-min]");
        nodes.forEach((node) => {
          const min = parseInt(node.dataset.ladderMin, 10);
          node.classList.toggle("is-unlocked", t.cans >= min);
          node.classList.toggle(
            "is-current",
            t.cans >= min &&
              !visible.some((v) => v.min > min && t.cans >= v.min),
          );
        });
        if (this.ladderFill) {
          const pct = Math.max(0, Math.min(100, (t.cans / maxMin) * 100));
          this.ladderFill.style.width = pct + "%";
        }

        /* "X% OFF TODAY" badge */
        if (this.offBadge) {
          if (t.pct > 0) {
            this.offBadge.hidden = false;
            this.offBadge.textContent = `${t.pct}% OFF TODAY`;
          } else {
            this.offBadge.hidden = true;
          }
        }

        /* free-shipping progress */
        if (this.freeshipEl && this.freeshipCents > 0) {
          if (t.final >= this.freeshipCents && t.cans > 0) {
            this.freeshipEl.hidden = false;
            this.freeshipEl.classList.add("is-unlocked");
            this.freeshipEl.textContent = "🚚 FREE shipping unlocked!";
          } else if (t.cans > 0) {
            const remaining = this.freeshipCents - t.final;
            this.freeshipEl.hidden = false;
            this.freeshipEl.classList.remove("is-unlocked");
            this.freeshipEl.textContent = `🚚 Add ${this.money(remaining)} more for FREE shipping`;
          } else {
            this.freeshipEl.hidden = true;
          }
        }
      }

      /* animate a money value ticking between totals */
      tickPrice(el, fromCents, toCents) {
        if (fromCents === toCents) {
          el.textContent = this.money(toCents);
          return;
        }
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          el.textContent = this.money(toCents);
          return;
        }
        const duration = 350;
        const start = performance.now();
        const step = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = this.money(Math.round(fromCents + (toCents - fromCents) * eased));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }

      /* mini cans stack up in the lineup strip */
      renderLineup() {
        if (!this.lineup || !this.lineupCans) return;
        this.lineupCans.innerHTML = "";
        let total = 0;
        this.state.forEach((entry) => {
          if (entry.qty === 0) return;
          const img = entry.row.querySelector(".rvx-bb__flavor-img");
          const accent = entry.row.style.getPropertyValue("--flavor-accent") || "#e8175a";
          for (let i = 0; i < entry.qty && total < 14; i++) {
            const can = document.createElement("span");
            can.className = "rvx-bb__lineup-can";
            can.style.setProperty("--can-accent", accent.trim());
            if (img) {
              const clone = img.cloneNode();
              clone.removeAttribute("class");
              can.appendChild(clone);
            }
            this.lineupCans.appendChild(can);
            total++;
          }
        });
        const overflow = Array.from(this.state.values()).reduce((n, e) => n + e.qty, 0) - total;
        if (overflow > 0) {
          const more = document.createElement("span");
          more.className = "rvx-bb__lineup-more";
          more.textContent = "+" + overflow;
          this.lineupCans.appendChild(more);
        }
        this.lineup.hidden = total === 0;
      }

      /* page accent shifts toward the dominant flavor */
      applyTakeover() {
        let best = null;
        let bestQty = 0;
        this.state.forEach((entry) => {
          if (!entry.crossSell && entry.qty > bestQty) {
            bestQty = entry.qty;
            best = entry;
          }
        });
        const accent = best
          ? (best.row.style.getPropertyValue("--flavor-accent") || "#e8175a").trim()
          : "#e8175a";
        this.style.setProperty("--rvx-takeover", accent);
        this.classList.toggle("has-takeover", bestQty > 0);
      }

      /* ---------- render ---------- */
      render() {
        const t = this.totals();

        /* tier hint — counts down to the next tier, then "max discount unlocked" */
        if (this.tierHint) {
          const next = this.nextTier(t.cans);
          const nextPct = next ? next[this.tierKey()] || 0 : 0;
          if (next && nextPct > t.pct) {
            const need = next.min - t.cans;
            this.tierHint.hidden = false;
            this.tierHint.classList.remove("is-max");
            this.tierHint.innerHTML =
              `Add <strong>${need}</strong> more ${need === 1 ? this.unitSingular : this.unitPlural} to unlock <strong>${nextPct}% off</strong>`;
          } else if (t.pct > 0) {
            this.tierHint.hidden = false;
            this.tierHint.classList.add("is-max");
            this.tierHint.innerHTML =
              `🔥 Max discount unlocked — <strong>${t.pct}% off</strong>`;
          } else {
            this.tierHint.hidden = true;
          }
        }

        /* summary */
        if (this.summary) {
          if (t.compare > 0) {
            this.summary.hidden = false;
            this.priceCompareEl.textContent = t.savings > 0 ? this.money(t.compare) : "";
            this.tickPrice(this.priceFinalEl, this.lastFinal, t.final);
            this.savingsEl.textContent =
              t.savings > 0 ? `YOUR SAVINGS: ${this.money(t.savings)}` : "";
          } else {
            this.summary.hidden = true;
          }
        }
        this.lastFinal = t.final;

        this.renderLineup();
        this.applyTakeover();
        this.updateLadder(t);
        this.updateStickyBar(t);

        /* CTA */
        if (this.cta) {
          const remaining = this.minCans - t.cans;
          if (remaining > 0) {
            this.cta.disabled = true;
            this.cta.classList.remove("is-ready");
            this.ctaText.textContent = this.ctaLockedTemplate.replace(/\d+/, remaining);
          } else {
            this.cta.disabled = false;
            this.cta.classList.add("is-ready");
            this.ctaText.textContent = this.preorder
              ? `PRE-ORDER NOW — ${this.money(t.final)}`
              : `ADD TO CART — ${this.money(t.final)}`;
          }
        }
      }

      /* ---------- cart ---------- */
      async addToCart() {
        const t = this.totals();
        if (t.cans < this.minCans) return;

        let planId = null;
        if (this.purchaseType === "subscribe") {
          if (this.subscribeAutoTier) {
            /* assign the plan matching the unlocked quantity tier */
            planId = this.planIdForPct(this.currentTierPct(t.cans));
          } else if (this.frequencySelect && this.frequencySelect.value) {
            planId = parseInt(this.frequencySelect.value, 10);
          }
        }

        const items = [];
        this.state.forEach((entry, variantId) => {
          if (entry.qty === 0) return;
          const vid = parseInt(variantId, 10);
          const item = { id: vid, quantity: entry.qty };
          if (planId && entry.plans.some((p) => p.id === planId)) {
            item.selling_plan = planId;
          } else if (planId && entry.plans.length > 0) {
            item.selling_plan = entry.plans[0].id;
          } else {
            /* One-time / pre-order: attach Stoq's pre-order selling plan so the
               order is a real pre-order line that Stoq tags + holds server-side.
               Without this, capture depends on Stoq's racy storefront retro-attach
               (which missed orders entirely — e.g. #1009). _Stoq.getSellingPlanId
               returns the plan id for pre-order variants and a falsy value once a
               variant is no longer on pre-order, so this self-deactivates at launch. */
            try {
              if (window._Stoq && typeof window._Stoq.getSellingPlanId === "function") {
                const stoqPlan = window._Stoq.getSellingPlanId(vid);
                if (stoqPlan) item.selling_plan = stoqPlan;
              }
            } catch (e) {}
          }
          items.push(item);
        });

        this.cta.classList.add("loading");
        this.cta.disabled = true;
        if (this.ctaError) this.ctaError.hidden = true;

        const cartDrawer = document.querySelector("cart-drawer");
        const body = { items };
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
          body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(this.cartUrl + ".js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (data.status && data.status !== 200) {
            throw new Error(data.description || data.message || "Could not add to cart.");
          }

          /* Tier discounts are AUTOMATIC (store-side): Shopify applies the best
             qualifying tier (35/40/45% at 3/6/9+) based on the live total
             quantity in the bundle collection — mix-and-match, edit-proof. No
             discount code needs to be applied from the theme. Subscriptions
             discount via their selling plan instead. */

          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "rivox-bundle-builder",
              cartData: data,
            });
          }

          if (cartDrawer && typeof cartDrawer.renderContents === "function") {
            cartDrawer.renderContents(data);
          } else {
            this.dispatchEvent(
              new CustomEvent("cart-drawer:refresh", {
                detail: { response: data },
                bubbles: true,
                composed: true,
              }),
            );
          }
        } catch (err) {
          if (this.ctaError) {
            this.ctaError.hidden = false;
            this.ctaError.textContent = err.message;
          }
        } finally {
          this.cta.classList.remove("loading");
          this.render();
        }
      }

      /* ---------- gallery ---------- */
      initGallery() {
        const slides = Array.from(this.querySelectorAll(".rvx-bb__gallery-slide"));
        const thumbs = Array.from(this.querySelectorAll(".rvx-bb__thumb"));
        if (slides.length < 2) return;
        let index = slides.findIndex((s) => s.classList.contains("is-active"));
        if (index < 0) index = 0;

        const show = (i) => {
          index = (i + slides.length) % slides.length;
          slides.forEach((s, si) => s.classList.toggle("is-active", si === index));
          thumbs.forEach((tb, ti) => tb.classList.toggle("is-active", ti === index));
          /* pause any videos in inactive slides */
          slides.forEach((s, si) => {
            if (si !== index) s.querySelectorAll("video").forEach((v) => v.pause());
          });
        };

        const prev = this.querySelector(".rvx-bb__gallery-arrow--prev");
        const next = this.querySelector(".rvx-bb__gallery-arrow--next");
        if (prev) prev.addEventListener("click", () => show(index - 1));
        if (next) next.addEventListener("click", () => show(index + 1));
        thumbs.forEach((tb) =>
          tb.addEventListener("click", () =>
            show(parseInt(tb.dataset.mediaIndex, 10)),
          ),
        );

        /* swipe support */
        const main = this.querySelector(".rvx-bb__gallery-main");
        let startX = null;
        main.addEventListener("touchstart", (e) => (startX = e.touches[0].clientX), { passive: true });
        main.addEventListener(
          "touchend",
          (e) => {
            if (startX === null) return;
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 40) show(dx < 0 ? index + 1 : index - 1);
            startX = null;
          },
          { passive: true },
        );
      }

      /* ---------- tabs ---------- */
      initTabs() {
        const btns = Array.from(this.querySelectorAll("[data-tab-btn]"));
        const panels = Array.from(this.querySelectorAll("[data-tab-panel]"));
        btns.forEach((btn) => {
          btn.addEventListener("click", () => {
            const i = btn.dataset.tabBtn;
            btns.forEach((b) => b.classList.toggle("is-active", b === btn));
            panels.forEach((p) =>
              p.classList.toggle("is-active", p.dataset.tabPanel === i),
            );
          });
        });
      }

      /* ---------- sticky add-to-cart bar ---------- */
      initStickyBar() {
        this.stickyBar = this.querySelector("[data-sticky-atc]");
        this.stickyPrice = this.querySelector("[data-sticky-price]");
        this.stickyCta = this.querySelector("[data-sticky-cta]");
        if (!this.stickyBar || !this.cta) return;

        if ("IntersectionObserver" in window) {
          const io = new IntersectionObserver(
            (entries) => {
              const ctaVisible = entries[0].isIntersecting;
              this.stickyBar.classList.toggle("is-visible", !ctaVisible);
              this.stickyBar.setAttribute("aria-hidden", ctaVisible ? "true" : "false");
            },
            { threshold: 0 },
          );
          io.observe(this.cta);
        }

        this.stickyCta.addEventListener("click", () => {
          const t = this.totals();
          if (t.cans >= this.minCans) {
            this.addToCart();
          } else {
            this.querySelector(".rvx-bb__flavors").scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
      }

      updateStickyBar(t) {
        if (!this.stickyBar) return;
        if (t.cans >= this.minCans) {
          this.stickyCta.textContent = this.preorder
            ? `PRE-ORDER NOW — ${this.money(t.final)}`
            : `ADD TO CART — ${this.money(t.final)}`;
          this.stickyPrice.textContent =
            t.savings > 0 ? `Saving ${this.money(t.savings)}` : "";
        } else {
          this.stickyCta.textContent = "BUILD YOUR BUNDLE";
          let minPrice = Infinity;
          this.state.forEach((e) => {
            if (!e.crossSell && e.price < minPrice) minPrice = e.price;
          });
          const pct = this.tiers.length ? this.tiers[0][this.tierKey()] || 0 : 0;
          if (minPrice < Infinity) {
            const from = Math.round(minPrice * (1 - pct / 100));
            this.stickyPrice.textContent = pct > 0
              ? this.tierKey() === "pct"
                ? `From ${this.money(from)} per ${this.unitSingular} with subscription`
                : `From ${this.money(from)} per ${this.unitSingular} in a bundle`
              : `From ${this.money(from)} per ${this.unitSingular}`;
          }
        }
      }

      /* ---------- batch bar ---------- */
      initBatchBar() {
        const batch = this.querySelector("[data-batch]");
        if (!batch) return;
        const daysEl = batch.querySelector("[data-batch-days]");
        const shipDate = batch.dataset.shipDate;
        if (!daysEl || !shipDate) return;
        const target = new Date(shipDate + "T00:00:00");
        if (isNaN(target.getTime())) return;
        const days = Math.max(0, Math.ceil((target - new Date()) / 86400000));
        daysEl.textContent = days === 0 ? "TODAY" : days === 1 ? "1 DAY" : days + " DAYS";
      }
    },
  );
}
